import { useCallback, useEffect, useMemo, useState } from "react";
import { MdDownload, MdRefresh } from "react-icons/md";
import attendanceService from "services/attendanceService";
import Button from "components/button";
import Card from "components/card";
import DataTable from "components/data-table";
import StatCard from "components/stat-card";
import { DatePicker, Select } from "components/fields";
import { PageContainer, PageHeader } from "components/page";
import { EmptyState, ErrorState } from "components/states";
import notify from "lib/notify";
import { getApiErrorMessage } from "api";
import { EMPTY, formatCurrency, formatDate, formatNumber } from "utils/format";

const SOURCE_OPTIONS = [
  { value: "all", label: "All Sources" },
  { value: "atrium", label: "Atrium Pulse" },
  { value: "churchmetrics", label: "Church Metrics" },
];

const CURRENCY_KEYWORDS = ["contribution", "fund", "giving", "offering", "tithe"];

const METRIC_LABELS = {
  attendance: "Attendance",
  kids: "Kids",
  salvations: "Salvations",
  volunteers: "Volunteers",
  contributions: "Contributions",
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);

  return {
    startDate: startDate.toISOString().split("T")[0],
    endDate: endDate.toISOString().split("T")[0],
  };
};

// Converts snake_case or camelCase metric keys to Title Case.
const formatMetricName = (key) =>
  METRIC_LABELS[key] ||
  key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .trim();

const isCurrencyMetric = (key) => {
  const lowerKey = key.toLowerCase();
  return CURRENCY_KEYWORDS.some((keyword) => lowerKey.includes(keyword));
};

const formatMetricValue = (metric, value) =>
  isCurrencyMetric(metric) ? formatCurrency(value) : formatNumber(value);

const sourceLabel = (row) => (row.source === "imported" ? "Church Metrics" : "Atrium Pulse");

const INITIAL_SORTING = [{ id: "date", desc: true }];

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState(() => ({
    ...getCurrentMonthRange(),
    source: "all",
  }));
  const [availableMetrics, setAvailableMetrics] = useState([]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await attendanceService.getMetricsSummary({
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
      const services = response.services || [];
      setReportData(services);
      setSummary({
        totals: response.totals || {},
        averages: response.averages || {},
      });

      const metricsSet = new Set();
      services.forEach((service) => {
        if (service.metrics) {
          Object.keys(service.metrics).forEach((key) => metricsSet.add(key));
        }
      });
      setAvailableMetrics(Array.from(metricsSet).sort());
    } catch (err) {
      console.error("Error loading report:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      loadReport();
    }
  }, [filters.startDate, filters.endDate, loadReport]);

  const filteredData = useMemo(() => {
    if (filters.source === "atrium") return reportData.filter((row) => row.source !== "imported");
    if (filters.source === "churchmetrics") return reportData.filter((row) => row.source === "imported");
    return reportData;
  }, [reportData, filters.source]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        sortDescFirst: false,
        sortingFn: (a, b) => new Date(a.original.date) - new Date(b.original.date),
        cell: ({ row }) => <span className="whitespace-nowrap">{formatDate(row.original.date)}</span>,
      },
      {
        id: "name",
        accessorFn: (row) => (row.name || "").toLowerCase(),
        header: "Service",
        sortDescFirst: false,
        cell: ({ row }) => <span className="whitespace-nowrap">{row.original.name}</span>,
      },
      {
        id: "campus",
        accessorFn: (row) => (row.campus || "").toLowerCase(),
        header: "Campus",
        sortDescFirst: false,
        meta: { className: "whitespace-nowrap text-fg-muted" },
        cell: ({ row }) => row.original.campus || EMPTY,
      },
      {
        accessorKey: "time",
        header: "Time",
        sortDescFirst: false,
        meta: { className: "whitespace-nowrap text-fg-muted" },
        cell: ({ row }) => attendanceService.formatTime(row.original.time),
      },
      {
        id: "source",
        accessorFn: (row) => (row.source || "").toLowerCase(),
        header: "Source",
        sortDescFirst: false,
        meta: { className: "whitespace-nowrap text-fg-muted" },
        cell: ({ row }) => sourceLabel(row.original),
      },
      ...availableMetrics.map((metric) => ({
        id: `metrics.${metric}`,
        accessorFn: (row) => row.metrics?.[metric] || 0,
        header: formatMetricName(metric),
        sortDescFirst: false,
        meta: { align: "right", className: "whitespace-nowrap" },
        cell: ({ getValue }) => formatMetricValue(metric, getValue()),
      })),
    ],
    [availableMetrics]
  );

  const setFilter = (key) => (e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  const setDate = (key) => (value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const exportToCSV = () => {
    if (reportData.length === 0) {
      notify.info("No data to export");
      return;
    }

    const headers = ["Date", "Service", "Campus", "Time", "Source", ...availableMetrics.map(formatMetricName)];
    const rows = reportData.map((row) => [
      formatDate(row.date),
      row.name,
      row.campus || "",
      row.time,
      sourceLabel(row),
      ...availableMetrics.map((metric) => row.metrics[metric] || 0),
    ]);

    let csv = headers.join(",") + "\n";
    rows.forEach((row) => {
      csv += row.map((cell) => `"${cell}"`).join(",") + "\n";
    });

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-report-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Reports"
        description="View and analyze attendance trends"
        actions={
          <>
            <Button variant="secondary" leftIcon={<MdRefresh />} onClick={loadReport} loading={loading}>
              Refresh
            </Button>
            <Button leftIcon={<MdDownload />} onClick={exportToCSV}>
              Export CSV
            </Button>
          </>
        }
      />

      <Card padding="md">
        <div className="flex flex-wrap items-end gap-4">
          <DatePicker
            label="Start Date"
            valueFormat="YYYY-MM-DD"
            value={filters.startDate}
            onChange={setDate("startDate")}
            className="w-full sm:w-52"
          />
          <DatePicker
            label="End Date"
            valueFormat="YYYY-MM-DD"
            value={filters.endDate}
            onChange={setDate("endDate")}
            className="w-full sm:w-52"
          />
          <Select
            label="Source"
            value={filters.source}
            onChange={setFilter("source")}
            options={SOURCE_OPTIONS}
            className="w-full sm:w-52"
          />
        </div>
      </Card>

      {error ? (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(error, "Could not load the attendance report")}
            onRetry={loadReport}
          />
        </Card>
      ) : (
        <>
          {summary && availableMetrics.length > 0 && (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              {availableMetrics.map((metric) => {
                const average = summary.averages[metric] || 0;
                return (
                  <StatCard
                    key={metric}
                    label={formatMetricName(metric)}
                    value={formatMetricValue(metric, summary.totals[metric] || 0)}
                    caption={`Avg: ${formatMetricValue(
                      metric,
                      isCurrencyMetric(metric) ? average : Math.round(average)
                    )}`}
                    loading={loading}
                    className="min-w-0"
                  />
                );
              })}
            </div>
          )}

          <DataTable
            aria-label="Attendance report"
            columns={columns}
            data={filteredData}
            loading={loading}
            sorting={INITIAL_SORTING}
            emptyState={
              <EmptyState
                variant="inline"
                title={
                  reportData.length === 0
                    ? "No data available for the selected date range"
                    : "No data available for the selected filters"
                }
              />
            }
          />
        </>
      )}
    </PageContainer>
  );
};

export default Reports;
